import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import AIVideoHeader from '../AIVideoHeader';
import AIVideoFooter from '../AIVideoFooter';
import VideoBackground from '../VideoBackground';

interface Step8LoadingProps {
  onNext: () => void;
  onBack: () => void;
}

const Step8Loading: React.FC<Step8LoadingProps> = ({ onNext, onBack }) => {
  const [progress, setProgress] = useState(51);
  const [hasCalledNext, setHasCalledNext] = useState(false);

  useEffect(() => {
    if (hasCalledNext) return; // Prevent multiple executions
    
    console.log('Step8Loading: Starting progress animation');
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95 && !hasCalledNext) {
          clearInterval(interval);
          setHasCalledNext(true);
          console.log('Step8Loading: Progress complete, calling onNext in 1 second');
          setTimeout(() => {
            console.log('Step8Loading: Calling onNext()');
            onNext();
          }, 1000);
          return 95;
        }
        return prev + Math.random() * 3;
      });
    }, 200);

    return () => {
      console.log('Step8Loading: Cleaning up interval');
      clearInterval(interval);
    };
  }, [hasCalledNext, onNext]);

  const fallbackBackground = (
    <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
      <motion.div
        className="absolute inset-0 opacity-20"
        animate={{
          background: [
            "radial-gradient(circle at 20% 50%, #ffffff 1px, transparent 1px)",
            "radial-gradient(circle at 80% 50%, #ffffff 1px, transparent 1px)",
            "radial-gradient(circle at 40% 50%, #ffffff 1px, transparent 1px)"
          ]
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
    </div>
  );

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col">
      <VideoBackground
        videoSrc="https://res.cloudinary.com/drinkamrs/video/upload/v1752753031/Characters_wole_ai_02_a_202507170450_g9mto0.mp4"
        fallbackComponent={fallbackBackground}
      />
      <AIVideoHeader 
        currentStep={8} 
        totalSteps={11} 
        onBack={onBack}
      />

      <div className="relative z-10 flex-1 px-4 py-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-sm mx-auto text-center"
        >
          {/* Progress Indicator */}
          <motion.div 
            className="relative w-24 h-24 mx-auto mb-6"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 96 96">
              <circle
                cx="48"
                cy="48"
                r="42"
                stroke="currentColor"
                strokeWidth="6"
                fill="none"
                className="text-gray-200/30"
              />
              <motion.circle
                cx="48"
                cy="48"
                r="42"
                stroke="url(#gradient)"
                strokeWidth="6"
                fill="none"
                strokeLinecap="round"
                initial={{ pathLength: 0.51 }}
                animate={{ pathLength: progress / 100 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                style={{
                  pathLength: progress / 100,
                }}
              />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="hsl(var(--hollywood-gold))" />
                  <stop offset="100%" stopColor="hsl(var(--hollywood-shimmer))" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-lg font-bold text-hollywood-gold">
                {Math.round(progress)}%
              </span>
            </div>
          </motion.div>

          {/* Loading Text */}
          <motion.h2 
            className="text-lg font-bold text-white mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Generating your personalized{' '}
            <span className="text-hollywood-gold">AI Video Creation Blueprint</span>...
          </motion.h2>

          {/* Testimonial Card */}
          <motion.div
            className="bg-black/30 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-hollywood-gold/30"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <h3 className="text-sm font-bold text-white mb-3">
              Join Thousands Amplifying Their Message with{' '}
              <span className="text-hollywood-gold">AI Video</span>!
            </h3>
            <div className="bg-gradient-to-r from-hollywood-gold/10 to-hollywood-shimmer/10 p-3 rounded-lg border border-hollywood-gold/20">
              <h4 className="font-semibold text-hollywood-shimmer mb-2 text-sm">
                "My Content Game Changed Overnight!"
              </h4>
              <p className="text-gray-300 text-xs leading-relaxed mb-2">
                "I used to spend hours on a single video. Now, with iSelect Digital's AI techniques, I produce high-quality content in minutes."
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-400">
                  - Alex P., Content Creator
                </span>
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3 h-3 fill-hollywood-gold text-hollywood-gold" />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <AIVideoFooter />
    </div>
  );
};

export default Step8Loading;